/**
 * Story `fe-be-monteur-s5d-offline-en-achtergrond` (EPIC-021, S021.5d) §A.6 — de store-singleton,
 * de klok-seam en HET wispunt. Een eigen module (zonder de allowlist en de projecties), zodat de
 * auth-laag (`sessionResume`, `AuthProvider`) kan wissen zonder de rest mee te laden.
 */
import { createFitterOfflineStore, createMemoryOfflineStore, type FitterOfflineStore } from './store';
import type { OfflineWipeReason } from './types';

let instance: FitterOfflineStore | null = null;

/** De ene offline-store van deze app (lazy). Zonder IndexedDB: in het geheugen. */
export function getFitterOfflineStore(): FitterOfflineStore {
    if (!instance) {
        instance = typeof indexedDB !== 'undefined' ? createFitterOfflineStore(indexedDB) : createMemoryOfflineStore();
    }
    return instance;
}

/** Alleen voor tests: vervang (of reset met `null`) de singleton. */
export function setFitterOfflineStoreForTests(store: FitterOfflineStore | null): void {
    instance = store;
}

/** De klok-seam (PB-3): tests zetten hem vast; productie gebruikt de wandklok. */
let clock: () => number = () => Date.now();

/** De huidige tijd volgens de offline-module. */
export function offlineNow(): number {
    return clock();
}

/** Alleen voor tests: zet de klok (of `null` = terug naar de wandklok). */
export function setOfflineClockForTests(next: (() => number) | null): void {
    clock = next ?? (() => Date.now());
}

/**
 * Gate O-1 — hoe lang een uitlogroute hooguit op het wissen wacht. Daarna gaat het uitloggen
 * (netwerk-logout, navigatie) door en loopt het wissen op de achtergrond verder. Het lokale
 * wis-tellertje in de store gaat bij de aanroep METEEN omhoog, dus een lopende lading kan ook
 * tijdens dat wachten niets meer terugzetten; en lezen toetst de eigenaar opnieuw.
 */
export const LOGOUT_WIPE_MAX_WAIT_MS = 1000;

/**
 * Wacht op `work`, maar hooguit `ms`. Een IndexedDB die blijft hangen (een ander tabblad houdt
 * een oude versie open, opslag stuk) mag uitloggen nooit tegenhouden.
 */
export function waitAtMost(work: Promise<unknown>, ms: number = LOGOUT_WIPE_MAX_WAIT_MS): Promise<void> {
    return new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, ms);
        work.then(
            () => {
                clearTimeout(timer);
                resolve();
            },
            () => {
                clearTimeout(timer);
                resolve();
            },
        );
    });
}

/**
 * §A.6 — HET wispunt van de offline-cache. Doet intern `store.clear()` (nooit `deleteDatabase`,
 * TC-SCAN-09) en hoogt de generatie op. De foto-wachtrij blijft ALTIJD staan (PB-2).
 *
 * De aanroepers vormen een GESLOTEN lijst (W1-W10, scan TC-SCAN-OFF-03); de reden zegt welke.
 * Faalt stil: de eigenaar-toets bij het lezen is het tweede slot.
 */
export async function clearFitterOfflineCache(_reason: OfflineWipeReason): Promise<void> {
    // De reden is er voor de scan en voor wie de code leest; het wissen is voor elke reden gelijk.
    try {
        await getFitterOfflineStore().clear();
    } catch {
        /* opslag onbereikbaar: lezen toont toch nooit gegevens van een ander */
    }
}
